import { kebabCase } from 'lodash';
import React, { useMemo, useState } from 'react';
import { ButtonDropdown, DropdownItem, DropdownToggle, DropdownMenu } from 'reactstrap';
import PropTypes from 'prop-types';
import { checkPermission, makeResource } from '../../../behaviors/src/lib/permissions';
import Icon from '../common/containers/icon';
import CabinetActionModal from './cabinetActionModal';
import FormModal from './formModal';
const CabinetActionsMenu = ({
  actionsMenuItems,
  availableFormTypeIdentifiers: _availableFormTypeIdentifiers = [],
  onBulkAction,
  cabinetIdentifier: _cabinetIdentifier = '',
  metaDeleted: _metaDeleted = 0,
  permissions: _permissions = {},
  selected: _selected = [],
  selectedFormTypeIdentifiers: _selectedFormTypeIdentifiers = [],
  user: _user = {}
}) => {
  const [open, setOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const toggle = () => {
    setOpen(!open);
  };

  // Filter actions based on metaDeleted state
  // - Show 'Delete' only when viewing non-deleted forms (metaDeleted !== 1)
  // - Show 'Undelete' only when viewing deleted forms (metaDeleted === 1)
  const filteredActionsMenuItems = useMemo(() => actionsMenuItems.filter(item => {
    var _item$action;
    const actionType = (_item$action = item.action) === null || _item$action === void 0 ? void 0 : _item$action.type;
    if (actionType === 'bulkDelete') {
      return _metaDeleted !== 1;
    }
    if (actionType === 'bulkUndelete') {
      return _metaDeleted === 1;
    }
    return true;
  }), [actionsMenuItems, _metaDeleted]);

  /**
   *  Send the bulk action to the action layer
   */
  const handleButtonSelect = currentAction => {
    onBulkAction(_cabinetIdentifier, _selected, currentAction);
    setSelectedAction(null);
  };

  /**
   *  Handle the action menu item selection. If an `action` property
   *  is set handle the action using CabinetActionModal. If an
   *  `actions` property is set, run these as behaviors
   */
  const handleSelectedMenuItem = async currentMenuItem => {
    const {
      action,
      actions
    } = currentMenuItem;
    if (!action && !actions) {
      // eslint-disable-next-line no-console
      console.error('No action or actions property found in the menu item');
      return;
    }
    if (action) {
      setSelectedAction(action);
    } else if (actions) {
      // these should be behaviors, so call the onBulkAction function
      // to run these within the redux actions layer
      await onBulkAction(_cabinetIdentifier, _selected, actions);
    }
  };
  const isActionEnabledForSelection = (actionPermission, requiresSelection = true) => {
    if (!actionPermission) {
      return false;
    }
    const formPermissionPrefix = 'allow_form_';
    const normalizedPermission = actionPermission.startsWith(formPermissionPrefix) ? actionPermission.substring(formPermissionPrefix.length) : actionPermission.substring(actionPermission.lastIndexOf('_') + 1);
    const formTypeIdentifiers = requiresSelection ? _selectedFormTypeIdentifiers : _availableFormTypeIdentifiers;
    if (requiresSelection && !_selected.length) {
      return false;
    }
    if (!formTypeIdentifiers.length) {
      return false;
    }
    if (requiresSelection) {
      return formTypeIdentifiers.every(ftIdentfier => checkPermission(makeResource('form', ftIdentfier), normalizedPermission, _permissions, _user));
    }
    return formTypeIdentifiers.some(ftIdentfier => checkPermission(makeResource('form', ftIdentfier), normalizedPermission, _permissions, _user));
  };
  const selectedCount = useMemo(() => {
    const selectedSize = new Set(_selected).size;
    return selectedSize > 0 ? `(${selectedSize})` : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_selected.length]);
  const hasNoSelectionActions = useMemo(() => filteredActionsMenuItems.some(item => item.requiresSelection === false), [filteredActionsMenuItems]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ButtonDropdown, {
    id: "cabinetActionsMenu",
    "data-testid": "t-cam-button",
    isOpen: open,
    toggle: toggle,
    size: "sm"
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    disabled: _selected.length === 0 && !hasNoSelectionActions,
    caret: true
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    glyph: "bolt"
  }), " Actions ", selectedCount)), /*#__PURE__*/React.createElement(DropdownMenu, null, filteredActionsMenuItems.map(actionMenuItem => /*#__PURE__*/React.createElement(DropdownItem, {
    key: actionMenuItem.title,
    "data-testid": `t-cam-button-${kebabCase(actionMenuItem.title)}`,
    className: `t-cam-button-${kebabCase(actionMenuItem.title)}`,
    onClick: () => handleSelectedMenuItem(actionMenuItem),
    disabled: !isActionEnabledForSelection(actionMenuItem.permission, actionMenuItem.requiresSelection !== false),
    style: kebabCase(actionMenuItem.title) === 'delete' ? {
      color: 'red'
    } : {}
  }, /*#__PURE__*/React.createElement(Icon, {
    style: {
      width: '10%',
      marginRight: '5px'
    },
    glyph: actionMenuItem.icon
  }), " ", actionMenuItem.title)), selectedAction && /*#__PURE__*/React.createElement(CabinetActionModal, {
    selectedAction: selectedAction,
    shouldPluralizeText: _selected.length > 1,
    onClose: () => setSelectedAction(null),
    onConfirm: () => handleButtonSelect(selectedAction),
    cabinetIdentifier: _cabinetIdentifier
  }))), /*#__PURE__*/React.createElement(FormModal, null));
};
CabinetActionsMenu.propTypes = {
  actionsMenuItems: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    icon: PropTypes.string
  })).isRequired,
  availableFormTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  cabinetIdentifier: PropTypes.string,
  metaDeleted: PropTypes.number,
  onBulkAction: PropTypes.func.isRequired,
  permissions: PropTypes.shape({}),
  selectedFormTypeIdentifiers: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.arrayOf(PropTypes.string),
  user: PropTypes.shape({})
};
export default CabinetActionsMenu;